import React, {
  useState,
} from 'react';
import { InstanceStateData, LearningModuleStateData } from 'src/types/state/learningModule';
import ClientLogger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';
import { useMetrics } from 'src/metrics/useUserMetrics';
import { FeatureName } from 'src/metrics/metricsFields';
import VideoView from './video';
import { DocumentView } from './pdf';

interface LearningModuleContentViewProps {
  learningModule: LearningModuleStateData;
  unauthenticated?: boolean;
  clientId?: string;
}

export interface LearningModuleViewProps {
  learningModule: LearningModuleStateData;
  moduleInstance: InstanceStateData;
  handleLanguageSelect: (language: string) => void;
  unauthenticated?: boolean;
  clientId?: string;
}

const LearningModuleContentView = ({ learningModule, unauthenticated, clientId }: LearningModuleContentViewProps) => {
  const { instanceMap, defaultLanguage } = learningModule;

  // Fall back to first available language if defaultLanguage is not in instanceMap
  const initialLanguage = instanceMap[defaultLanguage]
    ? defaultLanguage
    : Object.keys(instanceMap)[0];

  if (initialLanguage && initialLanguage !== defaultLanguage) {
    ClientLogger.warn(ClientLog.IllegalInternalDataState, new Error(
      `defaultLanguage "${defaultLanguage}" not in instanceMap for module `
      + `${learningModule.moduleId}; falling back to "${initialLanguage}"`,
    ), {
      moduleId: learningModule.moduleId,
      defaultLanguage,
      fallbackLanguage: initialLanguage,
      availableLanguages: Object.keys(instanceMap),
      url: window.location.href,
    });
  }

  const [selectedLanguage, setSelectedLanguage] = useState(initialLanguage);
  const selectedInstance: InstanceStateData | undefined = selectedLanguage
    ? instanceMap[selectedLanguage]
    : undefined;
  const { clickMetric } = useMetrics();

  if (!selectedInstance) {
    const error = new Error(
      `No renderable instance for module ${learningModule.moduleId}`,
    );
    ClientLogger.error(ClientLog.IllegalInternalDataState, error, {
      moduleId: learningModule.moduleId,
      selectedLanguage: selectedLanguage ?? null,
      availableLanguages: Object.keys(instanceMap),
      url: window.location.href,
    });
    throw error;
  }

  const handleLanguageSelect = (language: string) => {
    clickMetric({
      feature: FeatureName.ModuleLanguageChange,
      metadata: {
        moduleId: learningModule.moduleId,
        previousLanguage: selectedLanguage,
        selectedLanguage: language,
        url: window.location.href,
      },
    });
    setSelectedLanguage(language);
  };

  const learningModuleViewBaseProps: LearningModuleViewProps = {
    learningModule,
    moduleInstance: selectedInstance,
    handleLanguageSelect,
    unauthenticated,
    clientId,
  };

  switch (selectedInstance.instanceType) {
    case 'VIDEO':
      return <VideoView {...learningModuleViewBaseProps} />;
    case 'DOCUMENT':
      return <DocumentView {...learningModuleViewBaseProps} />;
    default:
    {
      const typeErrorMessage = 'Unsupported learning module instance '
      + `type: ${selectedInstance.instanceType} for instanceId ${selectedInstance.instanceId}`;
      const error = new Error(typeErrorMessage);
      ClientLogger.error(ClientLog.IllegalInternalDataState, error, {
        moduleId: learningModule.moduleId,
        instanceId: selectedInstance.instanceId,
        instanceType: selectedInstance.instanceType,
        url: window.location.href,
      });
      throw error;
    }
  }
};

export default LearningModuleContentView;
